import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import {
  CheckIcon,
  DownloadIcon,
  DocumentIcon,
  RefreshIcon,
  SelectorIcon,
} from "@heroicons/react/outline";
import dayjs from "dayjs";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  cancelAPIRequest,
  classNames,
  getEmeteraiFileName,
  getErrorMessages,
  isLastTokenNull,
  restampEligible,
  downloadFileWithFallback,
} from "../../Utils";
import { Pagination } from "../../Components/Pagination";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { Button } from "../../Components/Button";
import { Modal } from "../../Components/Modal";
import {
  useEmeteraiDispatch,
  useEmeteraiState,
} from "../../Context/emeterai/context";
import {
  clearRetryStamp,
  requestListEmeteraiStampings,
  requestListEmeteraiStampingsByOwner,
  requestRetryStamp,
} from "../../Context/emeterai/actions";
import TableSkeleton from "../../Components/TableSkeleton";
import { Alert } from "../../Components/Alert";
import { DOCUMENT_STATUS } from "../../Constants";
import { useLocation, useNavigate } from "react-router-dom";
import { DateFilter, filterOptions } from "../../Components/DateFilter";
import { useAuthState } from "../../Context";
import Badge from "../../Components/Badge";
import { ModalAlert } from "../../Components/ModalAlert";
import { ModalConfirm } from "../../Components/ModalConfirm";

const EmeteraiHistory = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [documentStatus, setDocumentStatus] = useState(DOCUMENT_STATUS[0]);
  const [page, setpage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[1]);
  const [selectedMonth, setSelectedMonth] = useState({});
  const [selectedYear, setSelectedYear] = useState({});
  const [dateFilter, setDateFilter] = useState({});

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);

  const [selectedRetry, setselectedRetry] = useState({});
  const [showRetryModal, setshowRetryModal] = useState(false);
  const [showSuccessRetryModal, setshowSuccessRetryModal] = useState(false);
  const [showErrorRetryModal, setshowErrorRetryModal] = useState(false);

  const {
    request,
    stampings,
    error,
    nextTokens,
    loading,
    downloadError,
    retryLoading,
    retryError,
    retryStamp,
  } = useEmeteraiState();

  const { user } = useAuthState();

  const emeteraiDispatch = useEmeteraiDispatch();

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress({ loaded: transferredBytes, total: totalBytes });
    }
  };

  const onLoad = (updateFilter) => {
    if (request) {
      cancelAPIRequest(request);
    }
    setpage(1);
    if (documentStatus.value === "ALL") {
      if (user.id) {
        requestListEmeteraiStampingsByOwner(emeteraiDispatch, {
          limit: 10,
          page: 1,
          updatedAt: updateFilter,
          owner: user.id,
        });
      }
    } else {
      requestListEmeteraiStampings(emeteraiDispatch, {
        page: 1,
        updatedAt: updateFilter,
        status: documentStatus.value,
      });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      if (documentStatus.value === "ALL") {
        if (user.id) {
          requestListEmeteraiStampingsByOwner(emeteraiDispatch, {
            limit: 10,
            page: nextPage,
            updatedAt: dateFilter,
            owner: user.id,
            nextToken: nextTokens[nextPage],
          });
        }
      } else {
        requestListEmeteraiStampings(emeteraiDispatch, {
          page: nextPage,
          updatedAt: dateFilter,
          status: documentStatus.value,
          nextToken: nextTokens[nextPage],
        });
      }
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      if (documentStatus.value === "ALL") {
        if (user.id) {
          requestListEmeteraiStampingsByOwner(emeteraiDispatch, {
            limit: 10,
            page: prevPage,
            updatedAt: dateFilter,
            owner: user.id,
            nextToken: nextTokens[prevPage],
          });
        }
      } else {
        requestListEmeteraiStampings(emeteraiDispatch, {
          page: prevPage,
          updatedAt: dateFilter,
          status: documentStatus.value,
          nextToken: nextTokens[prevPage],
        });
      }
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      if (documentStatus.value === "ALL") {
        if (user.id) {
          requestListEmeteraiStampingsByOwner(emeteraiDispatch, {
            limit: 10,
            page: selectedPage,
            updatedAt: dateFilter,
            owner: user.id,
            nextToken: nextTokens[selectedPage],
          });
        }
      } else {
        requestListEmeteraiStampings(emeteraiDispatch, {
          page: selectedPage,
          updatedAt: dateFilter,
          status: documentStatus.value,
          nextToken: nextTokens[selectedPage],
        });
      }
    }
  };

  const downloadFile = async (key) => {
    setdownloading(true);
    try {
      await downloadFileWithFallback(
        key,
        onDownloadProgress,
        getEmeteraiFileName,
        downloadRef
      );
    } catch (error) {
      setdownloadDocError(error);
    } finally {
      setdownloading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get("status");

    if (!statusParam) {
      navigate("/emeterai/history?status=ALL");
    } else if (
      documentStatus !== statusParam &&
      DOCUMENT_STATUS.map((documentStatus) => documentStatus.value).includes(
        statusParam
      )
    ) {
      const selectedDocumentStatus = DOCUMENT_STATUS.find(
        (documentStatus) => documentStatus.value === statusParam
      );
      setDocumentStatus(selectedDocumentStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, documentStatus]);

  useEffect(() => {
    if (documentStatus?.value) {
      let updatedAt = {};

      if (selectedFilter.value === "TODAY") {
        const startOfDay = dayjs().startOf("day").toISOString();
        const endOfDay = dayjs().endOf("day").toISOString();
        updatedAt = {
          between: [startOfDay, endOfDay],
        };
      } else if (selectedFilter.value === "LAST_HOUR") {
        const startOfLastHour = dayjs().subtract(1, "hour").toISOString();
        const endOfNow = dayjs().toISOString();
        updatedAt = {
          between: [startOfLastHour, endOfNow],
        };
      } else if (selectedFilter.value === "YESTERDAY") {
        const startOfYesterday = dayjs()
          .subtract(1, "day")
          .startOf("day")
          .toISOString();
        const endOfYesterday = dayjs()
          .subtract(1, "day")
          .endOf("day")
          .toISOString();
        updatedAt = {
          between: [startOfYesterday, endOfYesterday],
        };
      } else if (selectedFilter.value === "WEEK") {
        updatedAt = {
          gt: dayjs().subtract(7, "day").toISOString().substring(0, 10),
        };
      } else if (selectedFilter.value === "MONTH") {
        updatedAt = {
          gt: dayjs().subtract(1, "month").toISOString().substring(0, 10),
        };
      } else if (selectedFilter.value === "OTHER") {
        if (selectedMonth.value && selectedYear.value) {
          const actualMonthValue = parseInt(selectedMonth.value, 10);
          const firstDayOfMonth = dayjs()
            .year(selectedYear.value)
            .month(actualMonthValue - 1)
            .startOf("month")
            .toISOString();
          const lastDayOfMonth = dayjs()
            .year(selectedYear.value)
            .month(actualMonthValue - 1)
            .endOf("month")
            .toISOString();

          updatedAt = {
            between: [firstDayOfMonth, lastDayOfMonth],
          };
        }
      }

      setDateFilter(updatedAt);
      onLoad(updatedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user.id,
    emeteraiDispatch,
    documentStatus?.value,
    selectedFilter,
    selectedMonth,
    selectedYear,
  ]);

  useEffect(() => {
    if (selectedRetry.id) {
      setshowRetryModal(true);
    } else {
      setshowRetryModal(false);
    }
  }, [selectedRetry.id]);

  useEffect(() => {
    if (retryStamp.id) {
      setselectedRetry({});
      setshowSuccessRetryModal(true);
    }
  }, [retryStamp.id]);

  useEffect(() => {
    if (!retryError) {
    } else {
      setselectedRetry({});
      setshowErrorRetryModal(true);
    }
  }, [retryError]);

  useEffect(() => {
    if (!downloadDocError) {
      setshowDownloadErrorModal(false);
    } else {
      setshowDownloadErrorModal(true);
    }
  }, [downloadDocError]);

  const renderProgressBar = (downloadProgress) => {
    const total = downloadProgress?.total || 1;
    const percentage = Math.round(
      ((downloadProgress?.loaded || 0) / total) * 100
    );

    return (
      <div
        className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
        style={{ width: `${percentage}%` }}
      >
        {`${percentage}%`}
      </div>
    );
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                {renderProgressBar(downloadProgress)}
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex flex-col justify-center items-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
          <p className="text-xs font-medium">
            {Math.round(downloadProgress?.total / 1024 || 0)} KB
          </p>
        </div>
      </div>
    </Modal>
  );

  const renderRetryDialogModal = () => {
    if (selectedRetry.id) {
      return (
        <ModalAlert
          open={showRetryModal}
          setOpen={(open) => {
            setshowRetryModal(open);
          }}
          onCancel={() => {
            cancelAPIRequest(request);
            setselectedRetry({});
          }}
          title="Retry E-Meterai Stamping"
          description="Are you sure to retry stamping?"
          type="warning"
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          loadingButton={retryLoading}
          disabledButton={retryLoading}
          onConfirm={() => {
            requestRetryStamp(emeteraiDispatch, {
              id: selectedRetry.id,
            });
          }}
        />
      );
    }
    return null;
  };

  const renderRetrySuccess = () => {
    if (retryStamp.id) {
      return (
        <ModalConfirm
          open={showSuccessRetryModal}
          type="SUCCESS"
          setOpen={(open) => {
            setshowSuccessRetryModal(open);
          }}
          title="Success"
          description="The stamping request is being processed, check the stamping result on the Stamping History page"
          onConfirm={() => {
            window.location.href = `/emeterai/detail/${retryStamp.id}`;
            clearRetryStamp(emeteraiDispatch);
          }}
        />
      );
    }
    return null;
  };

  const renderRetryError = () => {
    if (retryError) {
      return (
        <ModalConfirm
          open={showErrorRetryModal}
          type="ALERT"
          setOpen={(open) => {
            setshowErrorRetryModal(open);
          }}
          title="Error"
          description={getErrorMessages(retryError)}
          onConfirm={() => {
            clearRetryStamp(emeteraiDispatch);
          }}
        />
      );
    }
    return null;
  };

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col flex-wrap sm:flex-row sm:items-center sm:w-full justify-between gap-4">
        <div className="flex flex-col flex-wrap sm:flex-row gap-4">
          <div>
            <Listbox
              value={documentStatus}
              onChange={(status) => {
                setDocumentStatus(status);
                if (status.value === "ALL") {
                  setSelectedFilter({ value: "WEEK", label: "1 Week" });
                }
                navigate(`/emeterai/history?status=${status.value}`);
              }}
            >
              {({ open }) => (
                <>
                  <ListboxButton className="flex items-center justify-between w-full sm:w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm">
                    <span className="max-w-full mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {t(documentStatus?.label) || t(DOCUMENT_STATUS[0].label)}
                    </span>
                    <SelectorIcon className="w-5 h-5 text-gray-500" />
                  </ListboxButton>
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:w-auto">
                      {DOCUMENT_STATUS.map((status) => (
                        <ListboxOption
                          key={status.value}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pr-9"
                            )
                          }
                          value={status}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate"
                                  )}
                                >
                                  {t(status.label)}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
          <DateFilter
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        </div>
        <div className="flex flex-col gap-4 justify-around sm:flex-row">
          <div className="w-full">
            <a
              href="https://verification.peruri.co.id/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="w-full"
                type="button"
                title="Verify Document"
                outline
              />
            </a>
          </div>
          <div className="w-full justify-start sm:justify-end">
            <Button
              className="w-full"
              onClick={() => navigate("/emeterai")}
              title={
                user.emeteraiQuota
                  ? `${t("New Stamp")} (${user.emeteraiQuota} ${t("Quota")})`
                  : "New Stamp"
              }
            />
          </div>
        </div>
      </div>
      {downloadError && (
        <Alert
          title="File Error"
          type="warning"
          message={downloadError?.message || "Error fetching file"}
          errors={downloadError?.errors}
        />
      )}
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("File Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("File Type")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Doc Number")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Doc Date")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Serial Number")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Time")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Status")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <TableSkeleton rows={3} columns={8} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {stampings.length > 0 ? (
                      stampings.map((stamping) => {
                        return (
                          <tr key={stamping.id}>
                            <td className="px-4 py-2 text-sm text-left text-blue-600">
                              <a href={`/emeterai/detail/${stamping.id}`}>
                                {getEmeteraiFileName(
                                  stamping.file.key,
                                  stamping.id
                                )}
                              </a>
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700">
                              {stamping.documentType}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700">
                              {stamping.documentNumber}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {stamping.documentDate
                                ? dayjs(stamping.documentDate)
                                    .locale(i18n.language)
                                    .format("DD MMM YYYY")
                                : ""}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700">
                              {stamping.serialNumber &&
                              stamping.status === "SUCCESS"
                                ? stamping.serialNumber
                                : ""}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {dayjs(stamping.createdAt).format(
                                "DD-MMM-YYYY HH:mm"
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {stamping.status === "PENDING" ? (
                                <Badge title="Pending" type="alert" />
                              ) : null}
                              {stamping.status === "SUCCESS" ? (
                                <Badge title="Success" type="success" />
                              ) : null}
                              {stamping.status === "FAILED" ? (
                                <Badge title="Failed" type="danger" />
                              ) : null}
                              {stamping.status === "EXPIRED" ? (
                                <Badge title="Expired" type="secondary" />
                              ) : null}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {stamping.status === "SUCCESS" && 
                               stamping.file?.key !== 'Dihapus' && 
                               stamping.file?.key !== 'Deleted' && 
                               stamping.file?.key !== 'deleted' && (
                                <button
                                  onClick={() =>
                                    downloadFile(stamping.file.key)
                                  }
                                >
                                  <DownloadIcon className="h-6 w-6 text-blue-500" />
                                </button>
                              )}
                              {stamping.status === "FAILED" &&
                                restampEligible(stamping.error) &&
                                stamping.file?.key !== 'Dihapus' && 
                                stamping.file?.key !== 'Deleted' && 
                                stamping.file?.key !== 'deleted' && (
                                  <button
                                    onClick={() => setselectedRetry(stamping)}
                                  >
                                    <RefreshIcon className="h-6 w-6 text-amber-500" />
                                  </button>
                                )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <ErrorStateTable
                        colSpan={9}
                        error={error}
                        emptyTitle={
                          isLastTokenNull(nextTokens)
                            ? "No e-meterai stamping data"
                            : "Please click Next again to load the data"
                        }
                      />
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
      {renderFileModal()}
      {renderRetryDialogModal()}
      {renderRetrySuccess()}
      {renderRetryError()}
      {renderDownloadError()}
    </div>
  );
};

export default EmeteraiHistory;
